import {
  Button,
  makeStyles,
  TableBody,
  TableFooter,
  TableRow
} from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { CurrencyrateList_currencyrates_edges_node } from "@saleor/currencyrate/types/CurrencyrateList";
import { CurrencyrateListUrlSortField } from "@saleor/currencyrate/urls";
import { translateBoolean } from "@saleor/intl";
import { renderCollection } from "@saleor/misc";
import { ListActions, ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface CurrencyrateListListProps
  extends ListProps,
    ListActions,
    SortPage<CurrencyrateListUrlSortField> {
  currencyrate: CurrencyrateList_currencyrates_edges_node[];
  onApply: (currency: any) => any;
}

const numberOfColumns = 10;

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      apply: {
        width: 80
      },
      colName: {
        width: "auto"
      },
      displayoredr: {
        width: 80
      },
      updatedate: {
        width: 160
      }
    },
    apply: {
      color: "red"
    }
  }),
  { name: "CurrencyrateList" }
);

const CurrencyrateList: React.FC<CurrencyrateListListProps> = ({
  currencyrate,
  disabled,
  isChecked,
  onNextPage,
  onPreviousPage,
  onRowClick,
  pageInfo,
  selected,
  sort,
  toggle,
  toggleAll,
  toolbar,
  onSort,
  onApply
}) => {
  const classes = useStyles({});
  const intl = useIntl();
  return (
    <ResponsiveTable>
      <TableHead
        colSpan={numberOfColumns}
        selected={selected}
        disabled={disabled}
        items={currencyrate}
        toggleAll={toggleAll}
        toolbar={toolbar}
      >
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === CurrencyrateListUrlSortField.slug
              ? getArrowDirection(sort.asc)
              : undefined
          }
          arrowPosition="right"
          onClick={() => onSort(CurrencyrateListUrlSortField.slug)}
        >
          <FormattedMessage defaultMessage="Code" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === CurrencyrateListUrlSortField.name
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CurrencyrateListUrlSortField.name)}
        >
          <FormattedMessage defaultMessage="Name" description="Name'" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === CurrencyrateListUrlSortField.symbol
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CurrencyrateListUrlSortField.symbol)}
        >
          <FormattedMessage defaultMessage="Symbol" description="Symbol'" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === CurrencyrateListUrlSortField.rate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CurrencyrateListUrlSortField.rate)}
        >
          <FormattedMessage defaultMessage="ApplyRate" description="Rate'" />
        </TableCellHeader>
        <TableCellHeader className={classes.apply} />
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === CurrencyrateListUrlSortField.onlineRate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CurrencyrateListUrlSortField.onlineRate)}
        >
          <FormattedMessage
            defaultMessage="OnlineRate"
            description="OnlineRate'"
          />
        </TableCellHeader>
        <TableCellHeader
          className={classes.updatedate}
          direction={
            sort.sort === CurrencyrateListUrlSortField.updateDate
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CurrencyrateListUrlSortField.updateDate)}
        >
          <FormattedMessage
            defaultMessage="UpdateDate"
            description="UpdateDate"
          />
        </TableCellHeader>
        <TableCellHeader
          className={classes.displayorder}
          direction={
            sort.sort === CurrencyrateListUrlSortField.displayOrder
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CurrencyrateListUrlSortField.displayOrder)}
        >
          <FormattedMessage
            defaultMessage="displayOrder"
            description="displayOrder"
          />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colName}
          direction={
            sort.sort === CurrencyrateListUrlSortField.isPublished
              ? getArrowDirection(sort.asc)
              : undefined
          }
          onClick={() => onSort(CurrencyrateListUrlSortField.isPublished)}
        >
          <FormattedMessage
            defaultMessage="isPublished"
            description="isPublished"
          />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={numberOfColumns}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          currencyrate,
          currency => {
            const isSelected = currency ? isChecked(currency.id) : false;

            return (
              <TableRow
                selected={isSelected}
                hover={!!currency}
                key={currency ? currency.id : "skeleton"}
                onClick={currency && onRowClick(currency.id)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    disabled={disabled}
                    disableClickPropagation
                    onChange={() => toggle(currency.id)}
                  />
                </TableCell>
                <TableCell className={classes.colName}>
                  {currency ? currency.code : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colName}>
                  {currency ? currency.name : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colName}>
                  {currency ? currency.symbol : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colName} align="right">
                  {currency ? (
                    currency.rate +
                    "(" +
                    ((
                      Math.abs(
                        (currency.rate -
                          (currency.slug ? currency.slug.rate : 1)) /
                          currency.rate
                      ) * 100
                    ).toFixed(2) +
                      "%)")
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.apply} align="left">
                  {currency ? (
                    !!!(
                      Math.abs(
                        currency.rate - (currency.slug ? currency.slug.rate : 1)
                      ) < 0.000001
                    ) ? (
                      <Button
                        className={classes.apply}
                        onClick={onApply(currency)}
                      >
                        Apply
                      </Button>
                    ) : (
                      ""
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.colName} align="right">
                  {currency ? (
                    currency.slug ? (
                      currency.slug.rate
                    ) : (
                      1
                    )
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
                <TableCell className={classes.updatedate}>
                  {currency ? currency.updateDate : <Skeleton />}
                </TableCell>
                <TableCell className={classes.displayorder} align="center">
                  {currency ? currency.displayOrder : <Skeleton />}
                </TableCell>
                <TableCell className={classes.colName} align="center">
                  {currency ? (
                    translateBoolean(currency.isPublished, intl)
                  ) : (
                    <Skeleton />
                  )}
                </TableCell>
              </TableRow>
            );
          },
          () => (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No currencyrate found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

export default CurrencyrateList;
